import * as React from 'react';
import { cva } from 'class-variance-authority';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.6.
 *
 * ## Radix or by hand
 *
 * `@radix-ui/react-toast` is installed and already used across the rest of the
 * library (`dialog` under construction, `select.tsx`, `switch.tsx`), but here it
 * was decided NOT to use it. Two concrete reasons, not a preference:
 *
 * 1. **Radix's announcer is ephemeral, and the contract demands the opposite.**
 *    `ToastImpl` mounts a `VisuallyHidden` node with `aria-live` only when the
 *    toast opens, writes the text a frame later, and unmounts it 1s after it is
 *    announced (`ToastAnnounce`, `react-toast/dist/index.mjs`). It is a valid
 *    technique, but it is the one this contract explicitly asks to avoid: "an
 *    aria-live region has to exist BEFORE it has content […] the Toaster renders
 *    its containers empty at all times". With Radix that region does not exist
 *    until there is a message, and stops existing a second later — there is no
 *    way to ask it for the other model from outside, it is internal to
 *    `ToastImpl`.
 * 2. **Radix distinguishes by `type` ("foreground"/"background"), the contract
 *    by tone (4 values → 2 levels).** Mapping tone→type is trivial, but on top
 *    of (1) it would have left a layer fighting the primitive instead of leaning
 *    on it: Radix's announcer would still have mounted its own ephemeral
 *    `aria-live` per toast, duplicating (and desynchronising) the pair of
 *    persistent regions this file needs to show.
 *
 * What IS taken from Radix, because it is already solved and tested, is the
 * pause **pattern**: a timer with the remaining time in a ref, paused on
 * `pointermove`/`focusin` inside the node and resumed on
 * `pointerleave`/`focusout` when the focus does not stay inside (see `ToastImpl`
 * in the same file). Here it is applied per individual toast rather than at
 * whole-viewport level: there is no reason not to pause the one the user is
 * already reading without stopping the rest of the stack along the way.
 *
 * The state itself is controlled from outside (`ToasterProps` =
 * `{ toasts, onDismiss }`), which is precisely the model Radix does NOT offer
 * (its `Toast.Root` is an `open`/`onOpenChange` per toast, designed so each one
 * decides its own lifecycle) — another sign that it fitted better by hand than
 * wrapped.
 */

export type ToastTone = 'success' | 'info' | 'warning' | 'danger';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: React.ReactNode;
  /** ms. `null` = it persists until dismissed; it is `danger`'s default. */
  duration?: number | null;
  action?: { label: string; onClick: () => void };
}

export interface ToasterProps {
  toasts: readonly Toast[];
  onDismiss: (id: string) => void;
  className?: string;
}

/** ~5s: the time a `success`/`info`/`warning` gets before closing itself.
 * `danger` is not covered by this default (see `resolveDuration`). */
export const DEFAULT_TOAST_DURATION_MS = 5000;

/** One icon per tone, different in silhouette and not only in colour (WCAG
 * 1.4.1), just like `Callout`/`StatusBadge`. It is not imported from
 * `callout.tsx` so as not to create a cross dependency between two base-level
 * primitives. */
const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
};

/**
 * The four tones collapse into two `aria-live` urgency levels (contract §5.6): a
 * "saved" must not interrupt the reader, an error must.
 */
const TONE_ARIA_LIVE: Record<ToastTone, 'polite' | 'assertive'> = {
  success: 'polite',
  info: 'polite',
  warning: 'assertive',
  danger: 'assertive',
};

/**
 * A toast's effective duration. It is resolved here — and not when the object is
 * built in `useToast` — so `Toaster` is correct as well when something renders it
 * directly with a hand-made `Toast` (tests, or a future caller not going through
 * the hook): the "`danger` does not close itself" rule belongs to the component,
 * not to a side effect of who built the object.
 */
function resolveDuration(toast: Pick<Toast, 'tone' | 'duration'>): number | null {
  if (toast.duration !== undefined) {
    return toast.duration;
  }
  return toast.tone === 'danger' ? null : DEFAULT_TOAST_DURATION_MS;
}

const toastVariants = cva(
  // `shadow-modal`: a toast INTERRUPTS, so it shares the highest step with
  // `Dialog` and with the sidebar's drawer. It is the one level where the shadow
  // also says there is something behind that is not the important thing.
  //
  // `starting:` (Tailwind v4, mapping to `@starting-style`) animates the entry
  // with no JS: the browser interpolates from the "starting" state as soon as
  // the node is inserted. `motion-reduce:transition-none` turns it off for
  // whoever asked for less motion, just as `skeleton.tsx` does with its loading
  // animation.
  'pointer-events-auto relative flex w-full max-w-sm items-start gap-inset-sm rounded-md border bg-surface p-inset shadow-modal transition-[opacity,transform] duration-200 ease-standard starting:translate-y-1 starting:opacity-0 motion-reduce:transition-none',
  {
    variants: {
      tone: {
        success: 'border-success',
        info: 'border-info',
        warning: 'border-warning',
        danger: 'border-danger',
      },
    },
  },
);

const toastIconVariants = cva('size-icon-md shrink-0', {
  variants: {
    tone: {
      success: 'text-success',
      info: 'text-info',
      warning: 'text-warning',
      danger: 'text-danger',
    },
  },
});

interface ToastCardProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

/**
 * An individual toast, with its own self-closing timer.
 *
 * The timer is modelled with the remaining time in a ref (not with
 * `setInterval` and not by recomputing from scratch): `pause()` freezes what is
 * left, `resume()` starts a fresh `setTimeout` for that amount. Without this,
 * moving the mouse in and out would restart the whole clock every time, which is
 * worse than not pausing — a toast with an action would never finish closing if
 * the user hesitates for a moment with the mouse over it before deciding.
 */
function ToastCard({ toast, onDismiss }: ToastCardProps): React.JSX.Element {
  const Icon = TONE_ICON[toast.tone];
  const duration = resolveDuration(toast);

  const onDismissRef = React.useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const elementRef = React.useRef<HTMLLIElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const remainingRef = React.useRef<number | null>(duration);
  const startedAtRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const hoveringRef = React.useRef(false);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const startTimer = React.useCallback(
    (ms: number | null) => {
      clearTimer();
      if (ms === null) {
        return;
      }
      startedAtRef.current = Date.now();
      timerRef.current = setTimeout(() => onDismissRef.current(toast.id), ms);
    },
    [clearTimer, toast.id],
  );

  // It starts on mount and every time the effective duration changes in flight
  // (say, if whoever created it passes a new `duration` with the same id). When
  // it was paused, it is not restarted out from under the user: the next
  // `resume()` picks it up with the new duration.
  React.useEffect(() => {
    remainingRef.current = duration;
    if (!pausedRef.current) {
      startTimer(duration);
    }
    return clearTimer;
  }, [duration, startTimer, clearTimer]);

  const pause = React.useCallback(() => {
    if (remainingRef.current === null || pausedRef.current) {
      return;
    }
    pausedRef.current = true;
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
  }, [clearTimer]);

  const resume = React.useCallback(() => {
    if (remainingRef.current === null || !pausedRef.current) {
      return;
    }
    pausedRef.current = false;
    startTimer(remainingRef.current);
  }, [startTimer]);

  function handleMouseEnter(): void {
    hoveringRef.current = true;
    pause();
  }

  function handleMouseLeave(): void {
    hoveringRef.current = false;
    if (!elementRef.current?.contains(document.activeElement)) {
      resume();
    }
  }

  // React's `onFocus`/`onBlur` behave like native `focusin`/`focusout` (they
  // bubble), so putting them on the container is enough: focus on the action
  // button or on the close button fires them all the same.
  function handleFocus(): void {
    pause();
  }

  function handleBlur(event: React.FocusEvent<HTMLLIElement>): void {
    const next = event.relatedTarget;
    const focusStaysInside = next !== null && elementRef.current?.contains(next);
    if (!hoveringRef.current && !focusStaysInside) {
      resume();
    }
  }

  return (
    <li
      ref={elementRef}
      data-tone={toast.tone}
      className={cn(toastVariants({ tone: toast.tone }))}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Icon aria-hidden="true" className={cn(toastIconVariants({ tone: toast.tone }))} />
      <div className="flex flex-1 flex-col gap-stack-sm">
        <p className="text-body-s font-semibold text-fg">{toast.title}</p>
        {toast.description ? (
          <div className="text-body-s text-fg-muted">{toast.description}</div>
        ) : null}
        {toast.action ? (
          <div>
            <button
              type="button"
              onClick={toast.action.onClick}
              className="inline-flex items-center rounded-sm text-body-s font-medium text-action underline-offset-4 hover:underline focus-ring pointer-coarse:min-h-touch"
            >
              {toast.action.label}
            </button>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Cerrar notificación"
        className="inline-flex shrink-0 items-center justify-center rounded-sm p-stack-sm text-fg-muted hover:text-fg focus-ring pointer-coarse:min-h-touch pointer-coarse:min-w-touch"
      >
        <X aria-hidden="true" className="size-icon-sm" />
      </button>
    </li>
  );
}

/**
 * The application's only `Toaster`. `AppShell` mounts it through its
 * `AppToaster` (`app-shell.tsx`, near the layout's close, outside the scrolling
 * container) and nowhere else.
 *
 * It ALWAYS renders two `aria-live` containers, whether or not there are toasts
 * inside: if the region were created together with the first message, a screen
 * reader already observing the tree would not learn of the change — live regions
 * have to pre-exist. There are two and not one because the attribute goes on the
 * container: `polite` groups success/info, `assertive` warning/error, and mixing
 * them into a single region would force the whole group to share the highest
 * level (all `assertive`, interrupting for a "saved") or the lowest (an error
 * not announced with urgency).
 */
export function Toaster({ toasts, onDismiss, className }: ToasterProps): React.JSX.Element {
  const politeToasts = toasts.filter((toast) => TONE_ARIA_LIVE[toast.tone] === 'polite');
  const assertiveToasts = toasts.filter((toast) => TONE_ARIA_LIVE[toast.tone] === 'assertive');

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-stretch gap-stack-sm p-inset-lg sm:inset-x-auto sm:right-0 sm:items-end',
        className,
      )}
    >
      <ol
        aria-live="polite"
        aria-atomic="false"
        className="flex flex-col items-stretch gap-stack-sm sm:items-end"
      >
        {politeToasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </ol>
      <ol
        aria-live="assertive"
        aria-atomic="false"
        className="flex flex-col items-stretch gap-stack-sm sm:items-end"
      >
        {assertiveToasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </ol>
    </div>
  );
}
